/**
 * @module AdapterConfigForm
 * @package @geenius/adapters/solidjs-kobalte
 * @description Kobalte-backed adapter configuration form for the SolidJS
 * library variant.
 */

import type { AdapterDomain, DomainAdapterConfig } from "@geenius/adapters";
import { DOMAIN_LABELS, getProvidersForDomain } from "@geenius/adapters";
import type { JSX } from "solid-js";
import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  Show,
} from "solid-js";
import { cn, loadKobalteCore } from "../lib/kobalte";

export interface AdapterConfigFormProps {
  domain: AdapterDomain;
  initialConfig?: DomainAdapterConfig;
  onSave: (config: DomainAdapterConfig) => Promise<void> | void;
  onCancel?: () => void;
  class?: string;
  titleId?: string;
}

export function AdapterConfigForm(props: AdapterConfigFormProps): JSX.Element {
  const providers = getProvidersForDomain(props.domain);
  const [core] = createResource(loadKobalteCore);
  const Button = createMemo(() => core()?.Button?.Root);
  const TextField = createMemo(() => core()?.TextField);
  const [provider, setProvider] = createSignal(
    props.initialConfig?.provider ?? providers[0]?.id ?? "",
  );
  const [apiKey, setApiKey] = createSignal(props.initialConfig?.apiKey ?? "");
  const [baseUrl, setBaseUrl] = createSignal(
    props.initialConfig?.baseUrl ?? "",
  );
  const [isSaving, setIsSaving] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const selectedMeta = () => providers.find((entry) => entry.id === provider());
  const needsApiKey = () => selectedMeta()?.requiresApiKey ?? false;
  const needsBaseUrl = () => selectedMeta()?.requiresBaseUrl ?? false;

  createEffect(() => {
    if (!needsApiKey() && apiKey()) {
      setApiKey("");
    }
    if (!needsBaseUrl() && baseUrl()) {
      setBaseUrl("");
    }
  });

  const buttonClass = createMemo(() =>
    cn(
      "inline-flex items-center justify-center rounded-md text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/30 disabled:pointer-events-none disabled:opacity-50",
    ),
  );
  const inputClass =
    "w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-950 shadow-sm outline-none transition-colors placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20";

  const providerOptionClass = (id: string) =>
    cn(
      "flex items-start gap-4 rounded-lg border p-4 text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/30",
      provider() === id
        ? "border-sky-500/40 bg-sky-50 shadow-sm"
        : "border-slate-200 bg-slate-50 hover:border-sky-300 hover:bg-sky-50/60",
    );

  const tierClass = (tier: "pronto" | "lancio" | "studio") =>
    cn(
      "mt-2 inline-flex rounded px-2 py-0.5 text-[0.5rem] font-bold uppercase",
      tier === "pronto"
        ? "bg-emerald-50 text-emerald-700"
        : tier === "lancio"
          ? "bg-sky-50 text-sky-700"
          : "bg-slate-100 text-slate-600",
    );

  const handleSubmit = async (event: Event): Promise<void> => {
    event.preventDefault();
    setError(null);

    if (!provider()) {
      setError("Select a provider");
      return;
    }

    if (needsApiKey() && !apiKey().trim()) {
      setError("API key is required");
      return;
    }

    setIsSaving(true);
    try {
      const config: DomainAdapterConfig = { provider: provider() };
      if (needsApiKey() && apiKey().trim()) {
        config.apiKey = apiKey().trim();
      }
      if (needsBaseUrl() && baseUrl().trim()) {
        config.baseUrl = baseUrl().trim();
      }
      await props.onSave(config);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const renderButton = (buttonProps: {
    children: JSX.Element;
    class: string;
    disabled?: boolean;
    onClick?: () => void;
    type: "button" | "submit";
    ariaPressed?: boolean;
  }): JSX.Element => (
    <Show
      fallback={
        <button
          aria-pressed={buttonProps.ariaPressed}
          class={buttonProps.class}
          disabled={buttonProps.disabled}
          onClick={buttonProps.onClick}
          type={buttonProps.type}
        >
          {buttonProps.children}
        </button>
      }
      keyed
      when={Button()}
    >
      {(KobalteButton) => (
        <KobalteButton
          aria-pressed={buttonProps.ariaPressed}
          class={buttonProps.class}
          disabled={buttonProps.disabled}
          onClick={buttonProps.onClick}
          type={buttonProps.type}
        >
          {buttonProps.children}
        </KobalteButton>
      )}
    </Show>
  );

  const renderTextField = (field: {
    id: string;
    label: string;
    placeholder: string;
    type: "password" | "url";
    value: string;
    onChange: (value: string) => void;
  }): JSX.Element => (
    <Show
      fallback={
        <div class="space-y-1.5">
          <label
            class="block text-xs font-semibold uppercase text-slate-500"
            for={field.id}
          >
            {field.label}
          </label>
          <input
            autocomplete={field.type === "password" ? "off" : undefined}
            class={inputClass}
            id={field.id}
            onInput={(event) => field.onChange(event.currentTarget.value)}
            placeholder={field.placeholder}
            type={field.type}
            value={field.value}
          />
        </div>
      }
      keyed
      when={TextField()}
    >
      {(KobalteTextField) => (
        <KobalteTextField.Root
          class="space-y-1.5"
          id={field.id}
          onChange={field.onChange}
          value={field.value}
        >
          <KobalteTextField.Label class="block text-xs font-semibold uppercase text-slate-500">
            {field.label}
          </KobalteTextField.Label>
          <KobalteTextField.Input
            aria-label={field.label}
            autocomplete={field.type === "password" ? "off" : undefined}
            class={inputClass}
            id={field.id}
            onInput={(event) => field.onChange(event.currentTarget.value)}
            placeholder={field.placeholder}
            type={field.type}
          />
        </KobalteTextField.Root>
      )}
    </Show>
  );

  return (
    <form
      class={cn(
        "space-y-6 rounded-lg border border-slate-200 bg-white p-6 text-slate-950 shadow-sm",
        props.class,
      )}
      data-geenius-component="AdapterConfigForm"
      data-geenius-variant="solidjs-kobalte"
      noValidate
      onSubmit={handleSubmit}
    >
      <div>
        <h3 id={props.titleId} class="text-lg font-bold text-slate-950">
          Configure {DOMAIN_LABELS[props.domain]}
        </h3>
        <p class="mt-1 text-sm text-slate-500">
          Select and configure your preferred provider.
        </p>
      </div>

      <Show when={error()}>
        <div
          class="rounded-md border border-red-500/30 bg-red-50 px-4 py-3 text-sm text-red-700"
          role="alert"
        >
          {error()}
        </div>
      </Show>

      <div class="space-y-3">
        <span class="block text-xs font-semibold uppercase text-slate-500">
          Provider
        </span>
        <div class="grid gap-2 sm:grid-cols-2">
          <For each={providers}>
            {(providerMeta) =>
              renderButton({
                ariaPressed: provider() === providerMeta.id,
                class: providerOptionClass(providerMeta.id) ?? "",
                onClick: () => setProvider(providerMeta.id),
                type: "button",
                children: (
                  <div class="flex-1">
                    <span class="block text-sm font-bold text-slate-950">
                      {providerMeta.name}
                    </span>
                    <span class="mt-1 block text-xs leading-relaxed text-slate-500">
                      {providerMeta.description}
                    </span>
                    <span class={tierClass(providerMeta.tier)}>
                      {providerMeta.tier}
                    </span>
                  </div>
                ),
              })
            }
          </For>
        </div>
      </div>

      <Show when={needsApiKey()}>
        {renderTextField({
          id: `adapter-${props.domain}-apiKey`,
          label: "API Key",
          onChange: setApiKey,
          placeholder: `Enter ${selectedMeta()?.name ?? provider() ?? "provider"} API key`,
          type: "password",
          value: apiKey(),
        })}
      </Show>

      <Show when={needsBaseUrl()}>
        {renderTextField({
          id: `adapter-${props.domain}-url`,
          label: "Base URL",
          onChange: setBaseUrl,
          placeholder: "https://...",
          type: "url",
          value: baseUrl(),
        })}
      </Show>

      <div class="flex justify-end gap-3 pt-2">
        <Show when={props.onCancel}>
          {renderButton({
            class:
              cn(
                buttonClass(),
                "px-5 py-2.5 text-slate-500 hover:text-slate-950",
              ) ?? "",
            onClick: () => props.onCancel?.(),
            type: "button",
            children: "Cancel",
          })}
        </Show>
        {renderButton({
          class:
            cn(
              buttonClass(),
              "border border-sky-600/30 bg-sky-600 px-8 py-3 font-bold text-white shadow-sm hover:bg-sky-700",
            ) ?? "",
          disabled: isSaving() || !provider(),
          type: "submit",
          children: isSaving() ? "Saving..." : "Save Configuration",
        })}
      </div>
    </form>
  );
}
